import { useState } from 'react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { UnderlineDateInput } from '@/components/ui/underline-date-input'
import { UnderlineTextarea } from '@/components/ui/underline-textarea'
import {
  addInterval,
  type IntervalStep,
  commonIntervals,
  describeStep,
  nextScheduleStep,
  type ScheduleStep,
} from '@/db/domain/followUps'
import { fromZaLocalToUtcIso, toZaDateKey } from '@/lib/datetime'
import { formatShortRelativeDateTime } from '@/lib/relativeDateTime'
import type { FollowUpRow } from '@/screens/FollowUpsScreen'

export type FollowUpAction = 'complete' | 'snooze' | 'close' | 'reopen'

export interface FollowUpActionResult {
  note?: string
  /** Set for a snooze by interval. */
  step?: ScheduleStep
  /** Set for reopen, and for a snooze to a picked date. */
  dueAt?: string
  /**
   * Done on a scheduled follow-up, with "don't remind again" ticked — the
   * caller closes instead of re-arming. (One-offs need no flag: their done
   * already finishes them.)
   */
  closeForGood?: boolean
}

export interface FollowUpActionDialogProps {
  action: FollowUpAction
  row: FollowUpRow
  onCancel: () => void
  onConfirm: (result: FollowUpActionResult) => void
}

/** Snooze offers short pushes only — anything longer is really a reschedule. */
const snoozeOptions: IntervalStep[] = commonIntervals.filter(
  (step) => step.unit === 'day' || step.unit === 'week',
)

/**
 * The one dialog behind every follow-up action.
 *
 * Its job is to make the consequence legible *before* the click, because the
 * actions differ in exactly the way that matters: done keeps a scheduled
 * follow-up alive, close is what ends it. Saying so on the button is what stops
 * "done" being read as "gone".
 */
export function FollowUpActionDialog({
  action,
  row,
  onCancel,
  onConfirm,
}: FollowUpActionDialogProps) {
  const [note, setNote] = useState('')
  const [snoozeStep, setSnoozeStep] = useState<IntervalStep>(
    snoozeOptions[2] ?? { amount: 1, unit: 'week' },
  )
  // A picked day beats the interval chips; picking a chip clears it again.
  const [snoozeDay, setSnoozeDay] = useState('')
  const [reopenDay, setReopenDay] = useState(defaultReopenDay())
  const [closeForGood, setCloseForGood] = useState(false)

  const nextStep = nextScheduleStep(row)
  const copy = actionCopy(action, row, nextStep)

  const trimmed = note.trim()
  // A follow-up may only end once it has an answer: done/close want either an
  // existing thread note or one written right here. Snooze and reopen keep the
  // follow-up alive, so they stay note-optional.
  const needsAnswer =
    (action === 'complete' || action === 'close') && !row.notes?.length
  const canConfirm = !needsAnswer || Boolean(trimmed)

  const confirm = () => {
    if (!canConfirm) return
    onConfirm({
      note: trimmed || undefined,
      step: action === 'snooze' && !snoozeDay ? snoozeStep : undefined,
      dueAt:
        action === 'reopen'
          ? fromZaLocalToUtcIso(reopenDay, '08:00')
          : action === 'snooze' && snoozeDay
            ? fromZaLocalToUtcIso(snoozeDay, '08:00')
            : undefined,
      closeForGood:
        action === 'complete' && nextStep ? closeForGood : undefined,
    })
  }

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onCancel())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="text-[13px] font-medium text-slate-900">
              {row.title}
            </div>
            <div className="text-[12px] text-slate-500">{row.customerName}</div>
          </div>

          {row.notes?.length ? (
            <div className="space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Notes so far
              </span>
              <ul className="max-h-32 space-y-1 overflow-y-auto border-l-2 border-violet-200 pl-2">
                {row.notes.map((linkedNote) => (
                  <li key={linkedNote.id} className="text-[12px] text-slate-600">
                    {linkedNote.text}
                    <span className="text-[11px] text-slate-400">
                      {' — '}
                      {linkedNote.authorName}
                      {' · '}
                      {formatShortRelativeDateTime(linkedNote.createdAt)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {action === 'snooze' ? (
            <fieldset className="space-y-1.5">
              <legend className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Remind again
              </legend>
              <div className="flex flex-wrap gap-1.5">
                {snoozeOptions.map((step) => {
                  const isSelected =
                    !snoozeDay &&
                    step.amount === snoozeStep.amount &&
                    step.unit === snoozeStep.unit
                  return (
                    <button
                      key={`${step.amount}-${step.unit}`}
                      type="button"
                      className={
                        isSelected
                          ? 'rounded-full border border-blue-400 bg-blue-500 px-3 py-1 text-[12px] font-medium text-white'
                          : 'rounded-full border border-slate-300 bg-white px-3 py-1 text-[12px] text-slate-600 hover:bg-slate-50'
                      }
                      onClick={() => {
                        setSnoozeStep(step)
                        setSnoozeDay('')
                      }}
                    >
                      {describeStep(step)}
                    </button>
                  )
                })}
              </div>
              <div className="flex items-center gap-2 pt-1">
                <span className="shrink-0 text-[12px] text-slate-500">
                  or on a date
                </span>
                <UnderlineDateInput
                  className="max-w-[11rem]"
                  value={snoozeDay}
                  min={toZaDateKey(new Date().toISOString())}
                  aria-label="Snooze until date"
                  onChange={(event) => setSnoozeDay(event.target.value)}
                />
              </div>
            </fieldset>
          ) : null}

          {action === 'complete' && nextStep ? (
            <label className="flex items-start gap-2 text-[12.5px] text-slate-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={closeForGood}
                onChange={(event) => setCloseForGood(event.target.checked)}
              />
              <span>
                Don't remind again
                <span className="block text-[11px] text-slate-500">
                  Closes it for good instead of coming back{' '}
                  {describeStep(nextStep)}.
                </span>
              </span>
            </label>
          ) : null}

          {action === 'reopen' ? (
            <div className="space-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Due date
              </span>
              <UnderlineDateInput
                value={reopenDay}
                aria-label="Due date"
                onChange={(event) => setReopenDay(event.target.value)}
              />
            </div>
          ) : null}

          <div className="space-y-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Note for the customer's thread
            </span>
            <UnderlineTextarea
              value={note}
              aria-label="Note for the customer's thread"
              placeholder={
                needsAnswer ? 'What came of it?' : 'Optional — what came of it?'
              }
              onChange={(event) => setNote(event.target.value)}
            />
            {needsAnswer ? (
              <span className="text-[11px] text-amber-700">
                This follow-up has no notes yet — say what came of it
                before it ends.
              </span>
            ) : (
              <span className="text-[11px] text-slate-500">
                Anything written here is saved to {row.customerName}'s notes,
                not to the reminder.
              </span>
            )}
          </div>
        </div>

        <DialogFooter>
          <button
            type="button"
            className="inline-flex h-9 items-center rounded-lg border border-slate-300 bg-white px-3 text-[13px] text-slate-600 hover:bg-slate-50"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className={
              !canConfirm
                ? 'inline-flex h-9 items-center rounded-lg border border-slate-200 bg-slate-100 px-3 text-[13px] font-medium text-slate-400'
                : action === 'close'
                  ? 'inline-flex h-9 items-center rounded-lg border border-rose-300 bg-white px-3 text-[13px] font-medium text-rose-700 hover:bg-rose-50'
                  : 'inline-flex h-9 items-center rounded-lg border border-blue-400 bg-blue-500 px-3 text-[13px] font-medium text-white hover:bg-blue-600'
            }
            disabled={!canConfirm}
            onClick={confirm}
          >
            {action === 'complete' && nextStep && closeForGood
              ? 'Done, close for good'
              : action === 'snooze' && snoozeDay
                ? `Snooze until ${snoozeDay}`
                : copy.confirmLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function actionCopy(
  action: FollowUpAction,
  row: FollowUpRow,
  nextStep: ScheduleStep | undefined,
) {
  if (action === 'complete') {
    // The distinction the whole feature turns on: with a schedule, done means
    // "handled, see you next time"; without one it is the end of the line.
    return nextStep
      ? {
          title: 'Mark as done',
          description: `Handled for now. This will come back ${describeStep(nextStep)}.`,
          confirmLabel: `Done, remind ${describeStep(nextStep)}`,
        }
      : {
          title: 'Mark as done',
          description:
            'This is a one-off follow-up, so marking it done finishes it. Snooze instead if you still need to come back to it.',
          confirmLabel: 'Done, finish it',
        }
  }

  if (action === 'snooze') {
    return {
      title: 'Snooze',
      description: `Push this out without changing its schedule. Currently due ${describeDueDay(row.dueAt)}.`,
      confirmLabel: 'Snooze',
    }
  }

  if (action === 'reopen') {
    return {
      title: 'Reopen follow-up',
      description: 'Bring this back with a new due date.',
      confirmLabel: 'Reopen',
    }
  }

  return {
    title: 'Close follow-up',
    description:
      'Stops this coming back for good. Its history and any notes it prompted are kept.',
    confirmLabel: 'Close for good',
  }
}

function describeDueDay(dueAt: string): string {
  return toZaDateKey(dueAt)
}

/** Reopening defaults to a week out — soon enough to matter, not tomorrow. */
function defaultReopenDay(): string {
  return toZaDateKey(addInterval(new Date().toISOString(), { amount: 1, unit: 'week' }))
}
